import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

const COLLECTIONS = ["employees", "assessments", "trainings", "calendar", "training_needs"];
const ACTIONS = ["create", "update", "delete"];

const ACTION_STYLES = {
  create: "bg-green-50 text-green-700 border-green-200",
  update: "bg-amber-50 text-amber-700 border-amber-200",
  delete: "bg-red-50 text-[#C41E1E] border-red-200",
};

export default function AuditTrailTab() {
  const [collection, setCollection] = useState("all");
  const [action, setAction] = useState("all");
  const [changedBy, setChangedBy] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 25 });
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);

  const load = async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: 25 };
      if (collection !== "all") params.collection = collection;
      if (action !== "all") params.action = action;
      if (changedBy.trim()) params.changed_by = changedBy.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get("/admin/audit-log", { params });
      setData(res.data);
      setPage(p);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = () => load(1);
  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));

  return (
    <div className="space-y-4" data-testid="audit-trail-tab">
      <div className="bc-card px-4 py-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        <div>
          <div className="bc-label mb-1.5">Collection</div>
          <Select value={collection} onValueChange={setCollection}>
            <SelectTrigger data-testid="audit-filter-collection"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All collections</SelectItem>
              {COLLECTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="bc-label mb-1.5">Action</div>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="bc-label mb-1.5">Changed by (employee_id)</div>
          <input
            value={changedBy}
            onChange={(e) => setChangedBy(e.target.value)}
            placeholder="e.g. 24 or system_auto"
            data-testid="audit-filter-changed-by"
            className="w-full h-9 text-sm border border-[#E5E5E5] rounded-md px-3 focus:outline-none focus:ring-1 focus:ring-[#C41E1E]"
          />
        </div>
        <div>
          <div className="bc-label mb-1.5">From</div>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="audit-filter-date-from"
            className="w-full h-9 text-sm border border-[#E5E5E5] rounded-md px-3 focus:outline-none focus:ring-1 focus:ring-[#C41E1E]" />
        </div>
        <div>
          <div className="bc-label mb-1.5">To</div>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="audit-filter-date-to"
            className="w-full h-9 text-sm border border-[#E5E5E5] rounded-md px-3 focus:outline-none focus:ring-1 focus:ring-[#C41E1E]" />
        </div>
        <div className="col-span-2 md:col-span-5 flex justify-end">
          <button
            type="button"
            onClick={applyFilters}
            data-testid="audit-filter-apply"
            className="inline-flex items-center gap-1.5 bg-[#0D0D0D] hover:bg-black text-white text-xs font-medium rounded-sm px-4 py-2"
          >
            Apply filters
          </button>
        </div>
      </div>

      <div className="bc-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F9F9F9] border-b border-[#E5E5E5]">
              <th className="text-left px-5 py-2.5 bc-label">Date / Time</th>
              <th className="text-left px-5 py-2.5 bc-label">Action</th>
              <th className="text-left px-5 py-2.5 bc-label">Collection</th>
              <th className="text-left px-5 py-2.5 bc-label">Record</th>
              <th className="text-left px-5 py-2.5 bc-label">Changed By</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="5" className="px-5 py-8 text-center text-[#666666]"><Loader2 className="animate-spin inline" size={16} /></td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan="5" className="px-5 py-8 text-center text-[#666666]">No audit entries match these filters.</td></tr>
            )}
            {!loading && data.items.map((entry) => (
              <tr
                key={entry.id}
                onClick={() => setActive(entry)}
                data-testid={`audit-row-${entry.id}`}
                className="border-b border-[#E5E5E5] bc-row-hover cursor-pointer"
              >
                <td className="px-5 py-2.5 font-mono text-xs">{new Date(entry.changed_at).toLocaleString()}</td>
                <td className="px-5 py-2.5">
                  <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide border rounded-sm ${ACTION_STYLES[entry.action] || ""}`}>
                    {entry.action}
                  </span>
                </td>
                <td className="px-5 py-2.5 font-mono text-xs">{entry.collection}</td>
                <td className="px-5 py-2.5 font-mono text-xs">{entry.record_id}</td>
                <td className="px-5 py-2.5">{entry.changed_by_name} <span className="text-[#999999] text-xs">#{entry.changed_by}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-[#666666]" data-testid="audit-trail-total">{data.total} total entries</span>
        <div className="flex items-center gap-2">
          <button type="button" disabled={page <= 1} onClick={() => load(page - 1)} data-testid="audit-page-prev"
            className="p-1.5 border border-[#E5E5E5] rounded-sm disabled:opacity-40"><ChevronLeft size={14} /></button>
          <span className="text-xs">Page {page} of {totalPages}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => load(page + 1)} data-testid="audit-page-next"
            className="p-1.5 border border-[#E5E5E5] rounded-sm disabled:opacity-40"><ChevronRight size={14} /></button>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="audit-detail-modal">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {active.action.toUpperCase()} · {active.collection} · {active.record_id}
                </DialogTitle>
                <DialogDescription>
                  Full before/after state captured at the time of this change.
                </DialogDescription>
              </DialogHeader>
              <div className="text-xs text-[#666666] mb-2">
                {new Date(active.changed_at).toLocaleString()} by {active.changed_by_name} (#{active.changed_by})
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="bc-label mb-1.5">Before</div>
                  <pre className="text-[11px] bg-[#F9F9F9] border border-[#E5E5E5] rounded-sm p-3 overflow-auto max-h-96" data-testid="audit-before-state">
                    {active.before_state ? JSON.stringify(active.before_state, null, 2) : "null"}
                  </pre>
                </div>
                <div>
                  <div className="bc-label mb-1.5">After</div>
                  <pre className="text-[11px] bg-[#F9F9F9] border border-[#E5E5E5] rounded-sm p-3 overflow-auto max-h-96" data-testid="audit-after-state">
                    {active.after_state ? JSON.stringify(active.after_state, null, 2) : "null"}
                  </pre>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
